/**
 * localStorage service for ZIP code persistence
 */

import type { ZipStorageData } from "../types/transit.js";

const ZIP_STORAGE_KEY = "shipToZipData";
const ZIP_EXPIRATION_MS = 3600000; // 1 hour

/**
 * Get stored ZIP data from localStorage
 * @returns Stored data or null if invalid/expired
 */
export function getStoredZipData(): ZipStorageData | null {
  try {
    const stored = localStorage.getItem(ZIP_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored) as ZipStorageData;
    if (
      !data ||
      typeof data.zip !== "string" ||
      typeof data.timestamp !== "number"
    ) {
      return null;
    }

    const now = Date.now();
    if (now - data.timestamp > ZIP_EXPIRATION_MS) {
      localStorage.removeItem(ZIP_STORAGE_KEY);
      return null;
    }

    if (!/^\d{5}$/.test(data.zip)) {
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
}

/**
 * Save ZIP data to localStorage with current timestamp
 * @param zip - The ZIP code to save
 */
export function saveZipData(zip: string): void {
  try {
    if (/^\d{5}$/.test(zip)) {
      const data: ZipStorageData = {
        zip: zip,
        timestamp: Date.now(),
      };
      localStorage.setItem(ZIP_STORAGE_KEY, JSON.stringify(data));
    }
  } catch (e) {
    // Ignore localStorage errors
  }
}

/**
 * Clear stored ZIP data from localStorage
 */
export function clearZipData(): void {
  try {
    localStorage.removeItem(ZIP_STORAGE_KEY);
  } catch (e) {
    // Ignore localStorage errors
  }
}
